import { t } from "@lingui/macro";
import dynamic from "next/dynamic";
import React, { useMemo } from "react";
import styled from "styled-components";
import { Loge } from "~/applications/OfferSearch/Domain/GogaillePlacesOfDestination";
import { PlaceDetails } from "~/components/Hotel/PlaceDetails";
import { Marker } from "~/components/Map/MapboxWebGL";
import { PinPointKing } from "~/components/Map/PinPoint";
import { CenteredPulseLoader } from "~/guidelines/Loaders";
import mediaQueries from "~/guidelines/Theme/mediaQueries";

const MapboxWebGLDynamic = dynamic(
  () => import("~/components/Map/MapboxWebGL"),
  {
    ssr: false,
    loading: () => <CenteredPulseLoader />,
  },
);

const Section = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(7, 0)};
  background-color: ${({ theme }) => theme.color.background.lightFirst};

  ${mediaQueries.md()} {
    padding: ${({ theme }) => theme.spacing(11, 0)};
  }
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(7)};
  max-width: 1240px;
  margin: 0 auto;

  ${mediaQueries.md()} {
    gap: ${({ theme }) => theme.spacing(8)};
  }
`;

const MapRow = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(0, 5)};
  ${mediaQueries.md()} {
    padding: 0;
  }
`;

const MapContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 400px;
  border: 1px solid ${({ theme }) => theme.color.line.lightSecond};
  border-radius: 4px;
  overflow: hidden;

  ${mediaQueries.md()} {
    height: auto;
    aspect-ratio: 1280/500;
  }
`;

export const GogaillePlacesLocation = ({
  loges,
  gogaillePlacesAddressDetails,
  gogaillePlacesDistancesDetails,
  gogaillePlacesParkingsDetails,
}: {
  loges: Array<Loge>;
  gogaillePlacesAddressDetails: string;
  gogaillePlacesDistancesDetails: string;
  gogaillePlacesParkingsDetails: string;
}) => {
  const markers = useMemo<Array<Marker>>(() => {
    return [
      ...loges.map((oneLoge) => ({
        label: t`Hotel ${oneLoge.name}`,
        longitude: oneLoge.building.address.coordinate.longitude,
        latitude: oneLoge.building.address.coordinate.latitude,
        kind: PinPointKing.Loge,
      })),
    ];
  }, [loges]);

  const { longitude, latitude } = useMemo(() => {
    const longitude = markers.reduce((acc, oneMarker) => {
      return acc + oneMarker.longitude;
    }, 0);
    const latitude = markers.reduce((acc, oneMarker) => {
      return acc + oneMarker.latitude;
    }, 0);
    return {
      longitude: longitude / markers.length,
      latitude: latitude / markers.length,
    };
  }, [markers]);

  if (loges.length === 0) {
    return null;
  }

  return (
    <Section>
      <Inner>
        <MapRow>
          <MapContainer>
            {markers.length > 0 ? (
              <MapboxWebGLDynamic
                latitude={latitude}
                longitude={longitude}
                zoom={14.5}
                markers={markers}
              />
            ) : undefined}
          </MapContainer>
        </MapRow>
        <PlaceDetails
          logeAddress={loges[0].building.address}
          addressDetails={gogaillePlacesAddressDetails}
          distancesDetails={gogaillePlacesDistancesDetails}
          parkingsDetails={gogaillePlacesParkingsDetails}
        />
      </Inner>
    </Section>
  );
};
